import type { ForecastDay, HourlyForecast } from '../types/weather';

export interface WeatherCacheEntry {
  daily: ForecastDay[];
  hourly: HourlyForecast[];
  fetchedAt: string;
  lastUpdated: string;
}

const STORAGE_PREFIX = 'wt_weather_';

export function weatherCacheKey(userId: string, cityVisitId: string): string {
  return `${STORAGE_PREFIX}${userId}_${cityVisitId}`;
}

export function getWeatherCache(userId: string, cityVisitId: string): WeatherCacheEntry | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }

  const raw = localStorage.getItem(weatherCacheKey(userId, cityVisitId));
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as WeatherCacheEntry;
  } catch {
    return null;
  }
}

export function setWeatherCache(
  userId: string,
  cityVisitId: string,
  entry: WeatherCacheEntry
): void {
  if (typeof localStorage === 'undefined') {
    return;
  }

  localStorage.setItem(weatherCacheKey(userId, cityVisitId), JSON.stringify(entry));
}

export function clearWeatherCache(userId: string, cityVisitId: string): void {
  if (typeof localStorage === 'undefined') {
    return;
  }

  localStorage.removeItem(weatherCacheKey(userId, cityVisitId));
}

export function mergeDailyIntoCache(
  existing: ForecastDay[],
  incoming: ForecastDay[]
): ForecastDay[] {
  const byDate = new Map(existing.map((day) => [day.date, day]));
  for (const day of incoming) {
    byDate.set(day.date, day);
  }
  return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
}

export function mergeHourlyIntoCache(
  existing: HourlyForecast[],
  incoming: HourlyForecast[]
): HourlyForecast[] {
  const byKey = new Map(existing.map((hour) => [`${hour.date}:${hour.hour}`, hour]));
  for (const hour of incoming) {
    byKey.set(`${hour.date}:${hour.hour}`, hour);
  }
  return Array.from(byKey.values()).sort((a, b) => {
    const dateCompare = a.date.localeCompare(b.date);
    return dateCompare !== 0 ? dateCompare : a.hour - b.hour;
  });
}

export function findDailyInCache(cache: WeatherCacheEntry | null, dateYmd: string): ForecastDay | null {
  return cache?.daily.find((day) => day.date === dateYmd) ?? null;
}

export function filterHourlyForDate(cache: WeatherCacheEntry | null, dateYmd: string): HourlyForecast[] {
  return (cache?.hourly ?? [])
    .filter((hour) => hour.date === dateYmd)
    .sort((a, b) => a.hour - b.hour);
}

export function isCacheFresh(fetchedAt: string, nowMs: number, freshMs: number): boolean {
  const fetchedMs = Date.parse(fetchedAt);
  if (Number.isNaN(fetchedMs)) {
    return false;
  }
  return nowMs - fetchedMs < freshMs;
}

/** True when per-day TTL has not elapsed. */
export function isDayFreshUntil(freshUntil: string | null | undefined, nowMs: number): boolean {
  if (!freshUntil) {
    return false;
  }
  const untilMs = Date.parse(freshUntil);
  if (Number.isNaN(untilMs)) {
    return false;
  }
  return nowMs < untilMs;
}

export function isWithinRefreshThrottle(lastUpdated: string, nowMs: number, throttleMs: number): boolean {
  const updatedMs = Date.parse(lastUpdated);
  if (Number.isNaN(updatedMs)) {
    return false;
  }
  return nowMs - updatedMs < throttleMs;
}
